import OfficialBadge from './community/OfficialBadge';
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Plus } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import StoryViewer from './StoryViewer';
import { storiesApi } from '../services/storiesApi';

/**
 * StoriesTray — Instagram-style "rings" row pinned above the feed.
 * Own "add" ring first, then connections' stories (gradient ring = unseen).
 */
const StoriesTray = forwardRef(({ language = 'en', isGuest, currentUserId, onAddStory }, ref) => {
  const es = language === 'es';
  const [tray, setTray] = useState([]);
  const [viewerIdx, setViewerIdx] = useState(null);

  const load = useCallback(async () => {
    try { setTray(await storiesApi.tray()); } catch { setTray([]); }
  }, []);

  useEffect(() => { if (!isGuest) load(); }, [load, isGuest]);
  useEffect(() => {
    const h = () => { if (!isGuest) load(); };
    window.addEventListener('lumera:stories-changed', h);
    return () => window.removeEventListener('lumera:stories-changed', h);
  }, [load, isGuest]);
  useImperativeHandle(ref, () => ({ reload: load }));

  const openViewer = (uid) => {
    const idx = tray.findIndex((g) => g.user_id === uid);
    if (idx >= 0) setViewerIdx(idx);
  };

  // Split out "my" ring so the add button always sits first.
  const mine = tray.find((g) => g.is_me);
  const others = tray.filter((g) => !g.is_me);

  if (isGuest) return null;

  return (
    <div className="mb-1" data-testid="stories-tray">
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
        {/* My story: view existing (ring) + always-available add ("+") */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          <div className="relative w-16 h-16">
            <button
              onClick={() => (mine ? openViewer(currentUserId) : onAddStory?.())}
              className="w-16 h-16 rounded-full flex items-center justify-center"
              data-testid="stories-my-ring"
            >
              <span className={`absolute inset-0 rounded-full p-[2.5px] ${mine?.has_unseen ? 'bg-gradient-to-tr from-fuchsia-500 via-rose-500 to-amber-400' : 'bg-muted'}`}>
                <span className="block w-full h-full rounded-full bg-background p-[2px]">
                  <Avatar className="w-full h-full">
                    <AvatarFallback className="bg-primary/15 text-primary">{es ? 'Yo' : 'Me'}</AvatarFallback>
                  </Avatar>
                </span>
              </span>
            </button>
            <button
              onClick={() => onAddStory?.()}
              className="absolute -bottom-0.5 -right-0.5 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center border-2 border-background z-10"
              data-testid="stories-add-btn"
              aria-label={es ? 'Añadir historia' : 'Add story'}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          <span className="text-[11px] text-muted-foreground truncate max-w-[64px]">{es ? 'Tu historia' : 'Your story'}</span>
        </div>

        {/* Others */}
        {others.map((g) => {
          const initials = (g.author_name || '?').slice(0, 2).toUpperCase();
          return (
            <div key={g.user_id} className="flex flex-col items-center gap-1 shrink-0">
              <button onClick={() => openViewer(g.user_id)} className="w-16 h-16 rounded-full" data-testid={`stories-ring-${g.user_id}`}>
                <span className={`block w-full h-full rounded-full p-[2.5px] ${g.has_unseen ? 'bg-gradient-to-tr from-fuchsia-500 via-rose-500 to-amber-400' : 'bg-muted'}`}>
                  <span className="block w-full h-full rounded-full bg-background p-[2px]">
                    <Avatar className="w-full h-full">
                      {g.author_picture ? <AvatarImage src={g.author_picture} /> : null}
                      <AvatarFallback className="bg-primary/15 text-primary text-sm">{initials}</AvatarFallback>
                    </Avatar>
                  </span>
                </span>
              </button>
              <span className="text-[11px] text-muted-foreground truncate max-w-[64px] flex items-center gap-0.5">{g.author_name}{g.is_official && <OfficialBadge className="w-3 h-3" />}</span>
              {g.suggested && (
                <span className="text-[9px] uppercase tracking-wide text-primary/80 -mt-0.5" data-testid={`story-suggested-${g.user_id}`}>
                  {language === 'es' ? 'Sugerido' : 'Suggested'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {viewerIdx !== null && (
        <StoryViewer
          tray={tray}
          startUserIndex={viewerIdx}
          language={language}
          onClose={() => setViewerIdx(null)}
          onSeen={load}
        />
      )}
    </div>
  );
});

StoriesTray.displayName = 'StoriesTray';
export default StoriesTray;
